import { Button, FormikField, ModalStyle as Modal, toastSuccess } from "@hubrise/react-components"
import type { InputRef } from "@hubrise/react-components"
import { Form, Formik } from "formik"
import type { FormikErrors } from "formik"
import * as React from "react"
import { useTranslation } from "react-i18next"

import { useCreateCatalogMutation } from "shared/hooks/mutations"
import { ICatalogSummary } from "shared/hubrise/models"

interface CreateModalContentProps {
  catalogSummaries: Array<ICatalogSummary>
  close: () => void
}

const CreateModalContent = ({ catalogSummaries, close }: CreateModalContentProps): JSX.Element => {
  const { t } = useTranslation()

  const $nameRef = React.useRef<InputRef>(null)

  React.useEffect(() => $nameRef.current?.focus())
  const handleFocus = (): void => $nameRef.current?.select()

  type FormValues = { name: string }
  const initialValues: FormValues = { name: "" }

  const validate = ({ name }: FormValues): FormikErrors<FormValues> => {
    const errors: FormikErrors<FormValues> = {}
    if (name.length === 0) errors.name = t("validations.not_empty")
    if (catalogSummaries.map((s) => s.name).includes(name)) errors.name = t("validations.must_be_unique")
    return errors
  }

  const mutation = useCreateCatalogMutation({
    onSuccess: (_, { name }) => toastSuccess(t("catalogs.create.success", { name })),
  })

  const handleSubmit = ({ name }: FormValues) => {
    const locationHrId = undefined // TODO: support location level catalogs
    mutation.mutate({ locationHrId, name })
    close()
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validate={validate}>
      <Form>
        <Modal.Header>
          <Modal.Title>{t("catalogs.create.title")}</Modal.Title>
          <Modal.Close onClick={close} />
        </Modal.Header>

        <Modal.Body>
          <FormikField.Input
            ref={$nameRef}
            name="name"
            label={t("catalogs.create.name")}
            withMargin
            onFocus={handleFocus}
          />
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary" type="submit">
            {t("catalogs.create.action")}
          </Button>
          <Button variant="empty" onClick={close}>
            {t("general.cancel")}
          </Button>
        </Modal.Footer>
      </Form>
    </Formik>
  )
}

export default CreateModalContent
